import React, { useState } from "react";
import {
  useParams,
  useNavigate,
  Route,
  Routes,
  Navigate,
  useLocation,
} from "react-router-dom";
import { useSwipeable } from "react-swipeable";
import LessonHeader from "../components/Activities/LessonHeader";
import MCQ from "../components/Activities/MCQ";
import Match from "../components/Activities/Match";
import Write from "../components/Activities/Write";
import Fill from "../components/Activities/Fill";
import { useLessonStats } from "./UseLessonStats";
import { MdKeyboardDoubleArrowUp } from "react-icons/md";
import VideoPage from "./VideoPage";
import { callWithAuth, postActivityComplete, getUserInfo } from "../api/Api";
import { PostActivityCompleteRequest, UserVideoWord } from "@shared_types";
import { useQuery } from "@tanstack/react-query";
import ModuleComplete from "@/components/Motivation/ModuleComplete";
import SignupToContinue from "./SignupToContinue";
import { LoadingPage } from "./LoadingPage";
import { Button } from "@chakra-ui/react";
import "@/TransitionsVertical.css";
import { useSound } from "@/hooks/useSound";
import useIsTouchDevice from "@/hooks/useIsTouchDevice";
import { useWaterDroplets } from "@/hooks/useWaterDroplets";
import { useDictionaryStats } from "@/hooks/useDictionaryStats";
import { useLessonData } from "@/hooks/useLessonData";
import Speak from "@/components/Activities/Speak";
import Sentence from "@/components/Activities/Sentence";
import { shuffleArray, withRetry } from "@/lib/utils";
import { getPlatform } from "@/lib/iap";

interface ViewRoute {
  viewName: string;
  viewComponent: JSX.Element | null;
}

const MOTIVATION_PAGE = "yayy";
const SPEAK_COOLDOWN_KEY = "speakActivityCooldown";
const COOLDOWN_DURATION = 15 * 60 * 1000; // 15 minutes in milliseconds

// Helper functions for random activity selection
const getRandomActivities = (
  availableActivities: string[],
  count: number = 3
): string[] => {
  const shuffled = shuffleArray(availableActivities);
  return shuffled.slice(0, count);
};

const isSpeakActivityOnCooldown = (): boolean => {
  const cooldownEnd = localStorage.getItem(SPEAK_COOLDOWN_KEY);
  if (!cooldownEnd) return false;

  const now = Date.now();
  const cooldownEndTime = parseInt(cooldownEnd, 10);

  if (now < cooldownEndTime) {
    return true;
  } else {
    // Cooldown expired, remove from localStorage
    localStorage.removeItem(SPEAK_COOLDOWN_KEY);
    return false;
  }
};

const setSpeakActivityCooldown = (): void => {
  const cooldownEnd = Date.now() + COOLDOWN_DURATION;
  localStorage.setItem(SPEAK_COOLDOWN_KEY, cooldownEnd.toString());
};

const getSelectedActivitiesForLesson = (
  lessonId: number,
  availableActivities: string[]
): string[] => {
  const storageKey = `lesson_${lessonId}_activities`;
  const stored = localStorage.getItem(storageKey);

  if (stored) {
    const parsedStoredActivities = JSON.parse(stored);
    const areStoredActivitiesValid = parsedStoredActivities.every(
      (activity: string) => availableActivities.includes(activity)
    );

    if (areStoredActivitiesValid) {
      return parsedStoredActivities;
    }
  }

  // Filter out speak activity if on cooldown
  let activitiesToChooseFrom = availableActivities;
  if (isSpeakActivityOnCooldown()) {
    activitiesToChooseFrom = availableActivities.filter(
      (activity) => activity !== "speak"
    );
  }

  const selected = getRandomActivities(activitiesToChooseFrom, 3);
  localStorage.setItem(storageKey, JSON.stringify(selected));
  return selected;
};

const Lesson: React.FC = () => {
  const [animate, setAnimate] = useState(false);
  const { chapterId, lessonId } = useParams();
  const lessonIdNumber = Number(parseInt(lessonId || "0"));
  const chapterIdNumber = Number(parseInt(chapterId || "0"));
  const navigate = useNavigate();
  const isTouch = useIsTouchDevice();
  const { playCorrectSound } = useSound();
  const { addDroplets } = useWaterDroplets();
  const { addKnownWords, addLearningWords } = useDictionaryStats();

  const userInfoQuery = useQuery({
    queryKey: ["userInfo"],
    queryFn: () => {
      return callWithAuth(getUserInfo);
    },
  });
  const isTempUser = userInfoQuery.data?.isTemp;
  const platform = getPlatform();

  const { query: lessonDataQuery, updateLocalLessonData } =
    useLessonData(lessonIdNumber);
  const location = useLocation(); // Hook to get the current location
  const { getStats, handleAddStatValue } = useLessonStats(
    chapterId || "",
    lessonId || ""
  );

  const nextLessonId = lessonDataQuery.data?.nextLessonId;
  const lesson = lessonDataQuery.data?.lesson;
  const isLastLesson = lessonDataQuery.data?.isLastLesson;
  const isLastChapter = lessonDataQuery.data?.isLastChapter;
  const videoActivity = lessonDataQuery.data?.video;
  const guessActivity = lessonDataQuery.data?.guess;
  const matchActivity = lessonDataQuery.data?.match;
  const fillActivity = lessonDataQuery.data?.fill;

  const speakActivity = lessonDataQuery.data?.speak;
  const sentenceActivity = lessonDataQuery.data?.sentence;

  const courseId = lessonDataQuery.data?.courseId;
  const navigateToChapter = useNavigateUp();
  const writeActivity = lessonDataQuery.data?.write;
  const isHorizontal = lessonDataQuery.data?.video.aspectRatio === "16:9";

  const handleUpdateUserWords = (ds: UserVideoWord[]) => {
    updateLocalLessonData(ds);
  };
  // Handlers for each stat
  const handleAddDrops = (amount: number) => {
    handleAddStatValue("dropsAdded", amount);

    if (amount > 0) {
      setAnimate(true);
      addDroplets(amount);

      setTimeout(() => setAnimate(false), 500);
    }
  };

  const handleAddTime = (time: number) => {
    handleAddStatValue("listenTimeAdded", time);
  };

  const handleAddKnownWords = (amount: number) => {
    handleAddStatValue("knownWordsAdded", amount);
    if (amount !== 0) {
      addKnownWords(amount);
    }
  };

  const handleAddLearningWords = (amount: number) => {
    handleAddStatValue("learningWordsAdded", amount);
    if (amount !== 0) {
      addLearningWords(amount);
    }
  };

  const getCurrentActivityFromPath = () => {
    const pathParts = location.pathname.split("/");
    return pathParts[pathParts.length - 1]; // Get the last part of the path, e.g., 'video', 'guess'
  };

  const currentViewName = getCurrentActivityFromPath();

  const handlers = useSwipeable({
    onSwipedUp: () => goToNextView(),
    trackMouse: true,
    delta: 200,
  });

  const handleSpeakSkip = () => {
    setSpeakActivityCooldown();
    goToNextView();
  };

  const handleActivityFinished = async () => {
    // Play sound effect when activity is completed, if it's not a video activity
    if (currentViewName !== "video") {
      // This will automatically respect the user's sound settings
      playCorrectSound();
    }

    const activityName = currentViewName;
    const data: PostActivityCompleteRequest = {
      lessonId: lessonIdNumber,
      activityType: activityName,
    };

    const retryActivityComplete = () => {
      withRetry(() => callWithAuth(postActivityComplete, data), 7, 1000)
        .then((res) => {
          // reward droplets
          handleAddDrops(res?.waterDropletsAwarded || 0);
        })
        .catch((error) => {
          console.error(`Error completing activity:`, error);
        });
    };

    // Start the initial attempt without blocking
    retryActivityComplete();

    const delayBetweenActivitiesMs = 500;
    setTimeout(() => {
      goToNextView();
    }, delayBetweenActivitiesMs);
  };

  if (lessonDataQuery.isPending || userInfoQuery.isPending) {
    return <LoadingPage showText={false} />;
  }

  if (lessonDataQuery.isError && userInfoQuery.data?.isTemp) {
    return <SignupToContinue />;
  }

  if (!lesson) {
    // fail safe if its a free user we can send them to upgrade
    if (userInfoQuery.data?.isFree) {
      return <Navigate to={"/upgrade"} />;
    } else {
      return <Navigate to={"/error"} />;
    }
  }

  let viewRoutes: ViewRoute[] = [];

  const goToNextView = () => {
    document.documentElement.style.setProperty(
      "--slide-out-animation",
      "slide-out"
    );
    document.documentElement.style.setProperty(
      "--slide-in-animation",
      "slide-in"
    );

    const navigateToNext = () => {
      const currentIndex = viewRoutes.findIndex(
        (route) => route.viewName === currentViewName
      );
      if (currentViewName === MOTIVATION_PAGE) {
        if (nextLessonId == 0) {
          navigate("/home/learn");
          return;
        }
        navigate(`/chapter/${chapterId}/lesson/${nextLessonId}`);
        return;
      } else if (currentIndex >= 0 && currentIndex < viewRoutes.length - 1) {
        navigate(
          `/chapter/${chapterId}/lesson/${lessonId}/${
            viewRoutes[currentIndex + 1].viewName
          }`
        );
        return;
      } else {
        // when your done a chapter navigate to home learn
        navigate("/home/learn");
        return;
      }
    };

    // Check if startViewTransition is supported
    if (typeof document.startViewTransition === "function") {
      document.startViewTransition(navigateToNext);
    } else {
      // Directly navigate without transition
      navigateToNext();
    }
  };

  const goToPrevView = () => {
    document.documentElement.style.setProperty(
      "--slide-out-animation",
      "slide-out-reverse"
    );
    document.documentElement.style.setProperty(
      "--slide-in-animation",
      "slide-in-reverse"
    );
    document.startViewTransition(() => {
      const currentIndex = viewRoutes.findIndex(
        (route) => route.viewName === currentViewName
      );
      if (currentIndex > 0 && currentIndex <= viewRoutes.length - 1) {
        navigate(
          `/chapter/${chapterId}/lesson/${lessonId}/${
            viewRoutes[currentIndex - 1].viewName
          }`
        );
      } else {
        navigateToChapter();
      }
    });
  };

  if (!chapterId && !lessonId) return <Navigate to="/home/learn" />;

  if (videoActivity?.youtubeId == "") {
    window.alert("This video is not available, going back to the chapter.");
    return <Navigate to={`/chapter/${chapterId}`} />;
  }

  // Get available activities based on what data exists
  const availableActivities: string[] = [];
  if (guessActivity) availableActivities.push("guess");
  if (matchActivity) availableActivities.push("match");
  if (fillActivity) availableActivities.push("fill");
  if (writeActivity) availableActivities.push("write");
  if (speakActivity && speakActivity.approved)
    availableActivities.push("speak");
  if (sentenceActivity && sentenceActivity.approved)
    availableActivities.push("sentence");

  // Get the selected activities for this lesson (3 random activities)
  const selectedActivities = getSelectedActivitiesForLesson(
    lessonIdNumber,
    availableActivities
  );

  // Define all possible views
  const allViewRoutes: ViewRoute[] = [
    {
      viewName: "video",
      viewComponent: videoActivity ? (
        <VideoPage
          chapterId={parseInt(chapterId || "0")}
          lesson={lesson}
          videoActivity={videoActivity!}
          onActivityFinished={handleActivityFinished}
          onUpdateUserWords={handleUpdateUserWords}
          onTrackDropsAdded={handleAddDrops}
          onTrackKnownAdded={handleAddKnownWords}
          onTrackLearningAdded={handleAddLearningWords}
          onTrackListeningTime={handleAddTime}
          isHorizontal={isHorizontal}
        />
      ) : null,
    },
    {
      viewName: "guess",
      viewComponent: guessActivity ? (
        <MCQ
          guessActivity={guessActivity}
          lessonId={lessonIdNumber}
          onActivityFinished={handleActivityFinished}
        />
      ) : null,
    },
    {
      viewName: "match",
      viewComponent: matchActivity ? (
        <Match
          matchActivity={matchActivity}
          onActivityFinished={handleActivityFinished}
          lessonId={lessonIdNumber}
        />
      ) : null,
    },
    {
      viewName: "fill",
      viewComponent: fillActivity ? (
        <Fill
          fillInTheBlankActivity={fillActivity}
          onActivityFinished={handleActivityFinished}
          lessonId={lessonIdNumber}
          onTrackListeningTime={handleAddTime}
          isHorizontal={isHorizontal}
        />
      ) : null,
    },
    {
      viewName: "write",
      viewComponent: writeActivity ? (
        <Write
          lessonId={lessonIdNumber}
          writeActivity={writeActivity}
          onActivityFinished={handleActivityFinished}
        />
      ) : null,
    },
    {
      viewName: "speak",
      viewComponent: speakActivity ? (
        <Speak
          speakActivity={speakActivity}
          onActivityFinished={handleActivityFinished}
          onTrackListeningTime={handleAddTime}
          onSkip={handleSpeakSkip}
        />
      ) : null,
    },
    {
      viewName: "sentence",
      viewComponent: sentenceActivity ? (
        <Sentence
          sentenceActivity={sentenceActivity}
          onActivityFinished={handleActivityFinished}
          onTrackListeningTime={handleAddTime}
          lessonId={lessonIdNumber}
          validWordId={
            sentenceActivity.userVideoWords.length > 0
              ? sentenceActivity.userVideoWords[0].videoWordId
              : 0
          }
          onSkip={goToNextView}
        />
      ) : null,
    },
    {
      viewName: MOTIVATION_PAGE,
      viewComponent: (
        <ModuleComplete
          onAddDroplets={handleAddDrops}
          nextLessonId={nextLessonId || 0}
          isTempUser={isTempUser!}
          isLastChapter={isLastChapter || false}
          isLastLesson={isLastLesson || false}
          courseId={courseId}
          lessonStats={getStats()}
          lessonId={lessonIdNumber}
          lesson={lesson}
          chapterId={chapterIdNumber}
        />
      ),
    },
  ];

  // Create viewRoutes in the order: video -> selected activities (in random order) -> motivation
  viewRoutes = [];

  // Add video first
  const videoRoute = allViewRoutes.find((route) => route.viewName === "video");
  if (videoRoute && videoRoute.viewComponent) {
    viewRoutes.push(videoRoute);
  }

  // Add selected activities in their random selection order
  selectedActivities.forEach((activityName) => {
    const activityRoute = allViewRoutes.find(
      (route) => route.viewName === activityName
    );
    if (activityRoute && activityRoute.viewComponent) {
      viewRoutes.push(activityRoute);
    }
  });

  // Add motivation page last
  const motivationRoute = allViewRoutes.find(
    (route) => route.viewName === MOTIVATION_PAGE
  );
  if (motivationRoute && motivationRoute.viewComponent) {
    viewRoutes.push(motivationRoute);
  }

  if (viewRoutes.length == 0) {
    // fail safe if its a free user we can send them to upgrade
    if (userInfoQuery.data?.isFree) {
      return <Navigate to={"/upgrade"} />;
    } else {
      return <Navigate to={"/error"} />;
    }
  }

  const firstActivity = viewRoutes[0].viewName;

  return (
    <div
      {...handlers}
      className={`flex flex-col h-full w-full justify-between items-center overflow-hidden ${
        currentViewName === "video" || currentViewName === "fill"
          ? "bg-black"
          : ""
      }`}
    >
      <LessonHeader handleBack={goToPrevView} pulse={animate} />
      <main className="flex-grow flex w-full items-center justify-center">
        <Routes>
          {/* Redirect from base URL (e.g. /chapter/1/lesson/7) to the first activity */}
          <Route path="" element={<Navigate to={firstActivity} />} />

          {/* Dynamically render routes */}
          {viewRoutes.map(({ viewName, viewComponent }) => (
            <Route key={viewName} path={viewName} element={viewComponent} />
          ))}
        </Routes>
      </main>
      {currentViewName === "video" ||
      currentViewName === "fill" ||
      currentViewName === "speak" ||
      currentViewName === "sentence" ? null : (
        <footer
          className={`flex flex-col items-center justify-center  ${
            platform === "web" ? "bottom-padding-web" : "pb-5"
          }`}
        >
          {!userInfoQuery.data?.isTemp &&
            currentViewName !== MOTIVATION_PAGE && (
              <>
                {!isTouch ? (
                  <Button
                    onClick={goToNextView}
                    height="30px"
                    paddingX="6px"
                    paddingY="2px"
                    fontSize="13px"
                    colorScheme="orange"
                  >
                    Skip Activity
                  </Button>
                ) : (
                  <div className="w-full flex flex-col items-center justify-center">
                    <MdKeyboardDoubleArrowUp className="w-7 h-auto" />
                    <span className="text-[11px]">Swipe up to skip</span>
                  </div>
                )}
              </>
            )}
          <div className="text-base text-[var(--color-orange)] font-extrabold pt-2 font-[Poppins]">
            parrot®
          </div>
        </footer>
      )}
    </div>
  );
};

// Custom hook to navigate up one level
const useNavigateUp = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Function to navigate up one level in the URL hierarchy
  const navigateToChapter = () => {
    const pathParts = location.pathname.split("/"); // Split the URL by "/"
    const chapterPath = pathParts.slice(0, -3); // Remove the last part of the URL (e.g., "lesson/7/video")
    const newPath = chapterPath.join("/"); // Join the remaining parts back together
    navigate(newPath); // Navigate to the new path
  };

  return navigateToChapter;
};

export default Lesson;
